import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useQueryClient } from '@mint-lib/query-client';
import * as DataIngestion from '@mint-sdk/api-data-ingestion';

import { useEnhancedMutation, useTypedQuery } from '../hooks/index.js';

const WHITELISTED_EMAILS_LIMIT = 100;

export const useWhitelistedEmails = (search?: string) => {
  const baseQueryOptions =
    DataIngestion.WhitelistedEmails.getWhitelistedEmailsQueryOptions({});

  return useTypedQuery({
    ...baseQueryOptions,
    placeholderData: [],
    select(data) {
      if (!search) return data;
      const lowerSearch = search.toLowerCase();
      return data.filter((item) =>
        item.email.toLowerCase().includes(lowerSearch),
      );
    },
  });
};

export const useWhitelistedEmailsMeta = () => {
  const { data } = useWhitelistedEmails();
  const totalItems = data?.length ?? 0;

  return {
    totalItems,
    maxCount: WHITELISTED_EMAILS_LIMIT,
    canBeCreated: totalItems < WHITELISTED_EMAILS_LIMIT,
  };
};

export const useCreateWhitelistedEmail = () => {
  const { t } = useTranslation('@myn/import-hub');
  const queryClient = useQueryClient();

  const { queryKey } =
    DataIngestion.WhitelistedEmails.getWhitelistedEmailsQueryOptions({});

  return useEnhancedMutation({
    mutationFn: async (
      payload: DataIngestion.WhitelistedEmails.CreateWhitelistedEmailPayload,
    ) => {
      await DataIngestion.WhitelistedEmails.createWhitelistedEmail({
        data: payload,
      });
    },
    onSuccess: () => {
      enqueueSnackbar({
        variant: 'success',
        title: t('New email has been added successfully.'),
        removable: true,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    meta: {
      getErrorsMap: (t) => ({
        default: t('Failed to add whitelisted email. Please try again.'),
        EmailAlreadyExists: t(
          'This email already exists. Please choose a unique email to proceed.',
        ),
      }),
    },
  });
};

export const useUpdateWhitelistedEmail = () => {
  const { t } = useTranslation('@myn/import-hub');
  const queryClient = useQueryClient();

  const { queryKey } =
    DataIngestion.WhitelistedEmails.getWhitelistedEmailsQueryOptions({});

  return useEnhancedMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: DataIngestion.WhitelistedEmails.UpdateWhitelistedEmailPayload;
    }) => {
      await DataIngestion.WhitelistedEmails.updateWhitelistedEmail({
        urlParams: { id },
        data: payload,
      });
    },
    onSuccess: () => {
      enqueueSnackbar({
        variant: 'success',
        title: t('Email has been updated successfully.'),
        removable: true,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    meta: {
      getErrorsMap: (t) => ({
        default: t('Failed to update whitelisted email. Please try again.'),
        EmailAlreadyExists: t(
          'This email already exists. Please choose a unique email to proceed.',
        ),
      }),
    },
  });
};

export const useDeleteWhitelistedEmail = () => {
  const { t } = useTranslation('@myn/import-hub');
  const queryClient = useQueryClient();

  const { queryKey } =
    DataIngestion.WhitelistedEmails.getWhitelistedEmailsQueryOptions({});

  return useEnhancedMutation({
    mutationFn: async (id: string) => {
      await DataIngestion.WhitelistedEmails.deleteWhitelistedEmail({
        urlParams: { id },
      });
    },
    onSuccess: () => {
      enqueueSnackbar({
        variant: 'success',
        title: t('Email has been successfully deleted.'),
        removable: true,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    meta: {
      getErrorsMap: (t) => ({
        default: t('Failed to delete whitelisted email. Please try again.'),
      }),
    },
  });
};
